import React, { useState } from "react";
import toast from "react-hot-toast";
import { applyCouponApi, getAvailableCouponsApi } from "../utils/couponApi";

const PromoCodeSection = ({
  cartItems = [],
  appliedCoupon = null,
  onApplyCoupon = () => {},
  onRemoveCoupon = () => {},
  disabled = false,
}) => {
  const [couponInput, setCouponInput] = useState("");
  const [loadingApply, setLoadingApply] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");

  const handleApply = async (codeToApply) => {
    const code = (codeToApply || couponInput).trim().toUpperCase();
    if (!code) {
      toast.error("Please enter a promo code");
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    try {
      setLoadingApply(true);
      const res = await applyCouponApi(code, cartItems);

      if (res.status === false || res.valid === false) {
        toast.error(res.message || "Invalid or ineligible promo code");
        return;
      }

      // Handle backend response data structure
      const couponData = {
        code: code,
        discount_amount: Number(res.discount_amount || res.discount || 0),
        discount_type: res.coupon?.discount_type || res.discount_type || "PERCENTAGE",
        discount_value: res.coupon?.discount_value || res.discount_value,
        description: res.coupon?.description || res.description || `${code} Applied`,
        discounted_subtotal: res.discounted_subtotal,
      };

      onApplyCoupon(couponData);
      setCouponInput("");
      setShowModal(false);
      toast.success(res.message || `Promo code "${code}" applied successfully! 🎉`);
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Failed to apply promo code. Please check the code and try again.";
      toast.error(errorMsg);
    } finally {
      setLoadingApply(false);
    }
  };

  const handleRemove = () => {
    onRemoveCoupon();
    toast.success("Promo code removed");
  };

  const fetchAvailableCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const coupons = await getAvailableCouponsApi();
      setAvailableCoupons(coupons);
    } catch (err) {
      console.error("Error loading available coupons:", err);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const openCouponsModal = () => {
    setShowModal(true);
    fetchAvailableCoupons();
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  return (
    <div className="promo-section-wrapper mb-3">
      {/* ── APPLIED STATE ── */}
      {appliedCoupon ? (
        <div className="applied-coupon-card p-3 rounded-theme">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <span className="applied-coupon-icon">🏷️</span>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="applied-code-badge fw-bold text-success">
                    {appliedCoupon.code}
                  </span>
                  <span className="badge bg-success text-white" style={{ fontSize: "0.72rem" }}>
                    APPLIED
                  </span>
                </div>
                <div className="applied-discount-hint text-success small mt-1">
                  {appliedCoupon.discount_amount > 0 ? (
                    <>You are saving ₹{Math.round(appliedCoupon.discount_amount).toLocaleString()} on subtotal!</>
                  ) : (
                    <>Promo applied to your order</>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-danger remove-coupon-btn"
              onClick={handleRemove}
              disabled={disabled}
              title="Remove promo code"
            >
              ✕ Remove
            </button>
          </div>
        </div>
      ) : (
        /* ── INPUT STATE ── */
        <div className="promo-input-card p-3 rounded-theme bg-white border">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="fw-semibold text-theme-dark m-0" style={{ fontSize: "0.92rem" }}>
              <i className="fa fa-ticket me-1 text-success"></i> Have a Promo Code?
            </label>
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none fw-semibold view-offers-btn"
              onClick={openCouponsModal}
              style={{ fontSize: "0.82rem", color: "#2E7D32" }}
            >
              🏷️ View Offers
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleApply();
            }}
            className="input-group"
          >
            <input
              type="text"
              className="form-control promo-input text-uppercase"
              placeholder="ENTER PROMO CODE"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              disabled={disabled || loadingApply}
              maxLength={25}
            />
            <button
              type="submit"
              className="btn btn-apply-coupon"
              disabled={disabled || loadingApply || !couponInput.trim()}
            >
              {loadingApply ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                "Apply"
              )}
            </button>
          </form>
        </div>
      )}

      {/* ── AVAILABLE COUPONS MODAL ── */}
      {showModal && (
        <div className="modal-backdrop-custom" onClick={() => setShowModal(false)}>
          <div
            className="modal-card-custom"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-custom d-flex justify-content-between align-items-center pb-2 border-bottom">
              <h5 className="m-0 text-theme-dark fw-bold" style={{ fontSize: "1.1rem" }}>
                🎁 Available Coupons & Offers
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body-custom py-3" style={{ maxHeight: "65vh", overflowY: "auto" }}>
              {loadingCoupons ? (
                <div className="text-center py-4">
                  <span className="spinner-border text-success" role="status" />
                  <p className="mt-2 text-muted small">Loading exclusive offers...</p>
                </div>
              ) : availableCoupons.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted m-0">No active coupons available right now.</p>
                  <p className="text-secondary small">Stay tuned for exciting discounts and seasonal sales!</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {availableCoupons.map((coupon) => {
                    const isApplicable = coupon.can_use !== false;
                    const isCurrent = appliedCoupon?.code === coupon.code;

                    return (
                      <div
                        key={coupon.id || coupon.code}
                        className={`coupon-item-card p-3 rounded-theme border ${
                          isCurrent ? "border-success bg-light-green" : isApplicable ? "bg-white" : "bg-light opacity-75"
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className="coupon-code-tag px-2 py-1 rounded fw-bold">
                              {coupon.code}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary p-1 border-0"
                              onClick={() => handleCopy(coupon.code)}
                              title="Copy code"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {copiedCode === coupon.code ? "✅ Copied" : "📋 Copy"}
                            </button>
                          </div>
                          {isCurrent ? (
                            <span className="badge bg-success">Applied</span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-apply-modal px-3"
                              disabled={!isApplicable || loadingApply}
                              onClick={() => handleApply(coupon.code)}
                            >
                              APPLY
                            </button>
                          )}
                        </div>

                        <div className="coupon-desc fw-semibold text-theme-dark mb-1" style={{ fontSize: "0.95rem" }}>
                          {coupon.discount_type === "PERCENTAGE"
                            ? `${coupon.discount_value}% OFF`
                            : `FLAT ₹${coupon.discount_value} OFF`}
                          {coupon.max_discount_amount && coupon.discount_type === "PERCENTAGE" && (
                            <span className="text-muted fw-normal small ms-1">
                              (Up to ₹{coupon.max_discount_amount})
                            </span>
                          )}
                        </div>

                        {coupon.description && (
                          <div className="text-secondary small mb-2">{coupon.description}</div>
                        )}

                        <div className="d-flex flex-wrap align-items-center justify-content-between text-muted pt-2 border-top gap-1" style={{ fontSize: "0.76rem" }}>
                          {coupon.min_order_value > 0 ? (
                            <span>Min. order: ₹{coupon.min_order_value}</span>
                          ) : (
                            <span>No min order value</span>
                          )}

                          {coupon.valid_to && (
                            <span>Expires: {new Date(coupon.valid_to).toLocaleDateString()}</span>
                          )}
                        </div>

                        {!isApplicable && coupon.reason && (
                          <div className="alert-reason text-danger small mt-2 pt-1 border-top" style={{ fontSize: "0.78rem" }}>
                            ⚠️ {coupon.reason}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer-custom pt-2 border-top text-end">
              <button
                type="button"
                className="btn btn-sm btn-secondary rounded-pill px-4"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .applied-coupon-card {
          background: #e8f5e9;
          border: 1.5px dashed #2E7D32;
        }
        .applied-code-badge {
          font-family: monospace;
          font-size: 1rem;
          letter-spacing: 1px;
        }
        .applied-coupon-icon {
          font-size: 1.4rem;
        }
        .remove-coupon-btn {
          font-size: 0.78rem;
          border-radius: 20px;
          padding: 3px 10px;
        }
        .promo-input {
          font-family: monospace;
          letter-spacing: 1px;
          font-weight: 600;
          border: 1.5px solid #ced4da;
          font-size: 0.88rem;
        }
        .promo-input:focus {
          border-color: #2E7D32;
          box-shadow: 0 0 0 0.2rem rgba(46, 125, 50, 0.15);
        }
        .btn-apply-coupon {
          background-color: #2E7D32;
          color: white;
          font-weight: 600;
          font-size: 0.88rem;
          padding: 0.4rem 1.2rem;
          border: none;
        }
        .btn-apply-coupon:hover:not(:disabled) {
          background-color: #1b5e20;
          color: white;
        }
        .btn-apply-coupon:disabled {
          background-color: #a5d6a7;
          color: white;
          cursor: not-allowed;
        }
        .view-offers-btn:hover {
          text-decoration: underline !important;
        }
        .coupon-code-tag {
          font-family: monospace;
          letter-spacing: 1px;
          background: #f1f8e9;
          color: #2E7D32;
          border: 1px dashed #2E7D32;
          font-size: 0.88rem;
        }
        .btn-apply-modal {
          background-color: #2E7D32;
          color: white;
          font-weight: 700;
          font-size: 0.8rem;
          border-radius: 20px;
        }
        .btn-apply-modal:hover:not(:disabled) {
          background-color: #1b5e20;
          color: white;
        }
        .bg-light-green {
          background-color: #f1f8e9 !important;
        }
        .modal-backdrop-custom {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        .modal-card-custom {
          background: white;
          border-radius: 12px;
          max-width: 500px;
          width: 100%;
          padding: 1.2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
          animation: fadeIn 0.2s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PromoCodeSection;
